'use client';

import { useEffect, useRef } from 'react';
import type { KeyboardEventHandler, HTMLAttributes } from 'react';
import { cn } from '../utils/cn';

type TextEditorProps = {
  value?: string; // HTML string
  onChange?: (value: string) => void;
  lines?: number; // Minimum number of lines in height
  placeholder?: string;
  disabled?: boolean;
} & Omit<HTMLAttributes<HTMLDivElement>, 'onChange' | 'value'>;

export function TextEditor({
  value = '',
  onChange,
  className,
  lines = 3,
  placeholder,
  disabled = false,
  ...rest
}: TextEditorProps) {
  const ref = useRef<HTMLDivElement | null>(null);

  // Keep DOM in sync when value prop changes from outside
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== value) {
      ref.current.innerHTML = value;
    }
  }, [value]);

  const handleInput = () => {
    if (!ref.current || disabled) return;
    onChange?.(ref.current.innerHTML);
  };

  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (disabled) return;

    // Ctrl/Cmd + B => bold
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'b') {
      e.preventDefault();
      document.execCommand('bold', false);
      handleInput();
      return;
    }

    // Ctrl/Cmd + I => italic
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'i') {
      e.preventDefault();
      document.execCommand('italic', false);
      handleInput();
      return;
    }

    // Ctrl/Cmd + U => underline
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'u') {
      e.preventDefault();
      document.execCommand('underline', false);
      handleInput();
      return;
    }
  };

  const applyFormat = (command: string) => {
    if (disabled) return;
    document.execCommand(command, false);
    // Trigger change right after
    if (ref.current) {
      onChange?.(ref.current.innerHTML);
    }
    // Keep focus on editor
    ref.current?.focus();
  };

  const isFormatActive = (command: string): boolean => {
    if (typeof document === 'undefined') return false;
    return document.queryCommandState(command);
  };

  const isEmpty =
    !value || value.trim() === '' || value === '<br>' || value === '<br/>';

  return (
    <div
      className={cn(
        'border border-el-grey-300 rounded-4 overflow-hidden relative',
        className
      )}
    >
      {/* Toolbar */}
      {!disabled && (
        <div className="flex items-center gap-1 p-1 bg-el-grey-50 border-b border-el-grey-300">
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()} // avoid blurring editor
            onClick={() => applyFormat('bold')}
            className={cn(
              'px-2 py-1 text-12 font-bold border border-el-grey-300 rounded-2 hover:bg-el-grey-100 transition-colors',
              isFormatActive('bold') && 'bg-el-grey-200'
            )}
            title="Bold (Ctrl/Cmd + B)"
          >
            B
          </button>
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => applyFormat('italic')}
            className={cn(
              'px-2 py-1 text-12 italic border border-el-grey-300 rounded-2 hover:bg-el-grey-100 transition-colors',
              isFormatActive('italic') && 'bg-el-grey-200'
            )}
            title="Italic (Ctrl/Cmd + I)"
          >
            I
          </button>
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => applyFormat('underline')}
            className={cn(
              'px-2 py-1 text-12 underline border border-el-grey-300 rounded-2 hover:bg-el-grey-100 transition-colors',
              isFormatActive('underline') && 'bg-el-grey-200'
            )}
            title="Underline (Ctrl/Cmd + U)"
          >
            U
          </button>
        </div>
      )}

      {/* Editable area */}
      <div className="relative">
        <div
          ref={ref}
          contentEditable={!disabled}
          suppressContentEditableWarning
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          className={cn(
            'min-h-[3rem] outline-none px-2 py-1 text-12 w-full',
            disabled && 'bg-el-grey-100 cursor-not-allowed'
          )}
          style={{
            minHeight: `${lines * 1.5 + 0.5}rem`,
          }}
          {...rest}
        />
        {isEmpty && placeholder && !disabled && (
          <div
            className="absolute top-1 left-2 text-12 text-el-grey-400 pointer-events-none"
            style={{ paddingTop: '0.25rem' }}
          >
            {placeholder}
          </div>
        )}
      </div>
    </div>
  );
}
